import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import './LandingPage.css'

const LandingPage = () => {
  return (
    <div className="min-h-screen landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <nav className="nav">
            <Link to="/" className="logo">Samvadic</Link>
            <ul className="nav-links">
              <li><Link to="#framework">Framework</Link></li>
              <li><Link to="#journey">Journey</Link></li>
              <li><Link to="#pricing">Pricing</Link></li>
            </ul>
            <div>
              <Link to="/login" className="btn-secondary">Sign In</Link>
              <Link to="/register" className="btn-primary">Unlock Potential</Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-badge-large">AI-Powered Brand Clarity Platform</div>
          
          <div className="hero-container-split">
            <div className="hero-left">
              <div className="logo-space">{/* Space reserved for Samvadic logo/icon */}</div>
              <div className="meet-text">Meet</div>
              <h1 className="hero-title">Samvadic</h1>
            </div>
            
            <div className="hero-right">
              <p className="hero-subtitle-large">Samvadic empowers startup founders to unlock hidden potential, transcend functional limitations, and set up your brand for <span className="highlight-text">stronger impact</span> — giving you the strategic advantage you need.</p>
              
              <p className="hero-description">Built as a multi-agent AI system with one master orchestrator and four specialized expert sub-agents, we understand that founders focus on creating MVPs and getting products out. But with the right brand foundations, you can set yourself up for bigger success from the start.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Success Section */}
      <section className="brand-success">
        <div className="container">
          <div className="brand-success-grid">
            <div className="brand-success-content">
              <h2>The World's Biggest Brands Share a Secret</h2>
              <p>The most successful brands don't just sell products. They transcend the functional domain to play a stronger role for humans and the wider culture.</p>
              <p>They become essential to how people see themselves and navigate the world. This isn't accidental — it's strategic.</p>
              <p>This is where <em>Samvadic</em> comes in. The name stems from the Sanskrit word <strong>Samvad</strong> — meaning "meaningful dialogue toward enhanced clarity." We guide you to discover what larger human end your brand can serve.</p>
            </div>
            <div className="brand-success-box">
              <h3>Your Strategic Advantage</h3>
              <p>Most startups compete on features because they never discovered what stronger impact their solution can create. Samvadic reveals this hidden potential and roots your brand in human psychology & culture — creating unshakeable strategic anchors.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Section */}
      <section className="framework" id="framework">
        <div className="container">
          <h2 className="section-title">The Four Strategic Anchors</h2>
          <p className="section-subtitle">Our comprehensive methodology transforms your functional solution into a complete brand strategy, ensuring every tactical element serves your stronger impact while delivering measurable business results.</p>
          
          <div className="anchors-grid-single-line">
            <div className="anchor-card">
              <div className="anchor-icon">🎯</div>
              <h3>Truth Anchor</h3>
              <p>Your positioning foundation rooted in fundamental human truth. Discover the deeper human motivation your brand serves beyond functionality, creating unshakeable strategic advantage.</p>
            </div>
            <div className="anchor-card">
              <div className="anchor-icon">🎭</div>
              <h3>Expression Anchor</h3>
              <p>How your truth manifests through authentic brand identity. Develop voice, personality, and visual elements that bridge functional benefits to meaningful human connection.</p>
            </div>
            <div className="anchor-card">
              <div className="anchor-icon">⚡</div>
              <h3>Attention Anchor</h3>
              <p>Your channel strategy for earning lasting presence. Build go-to-market approach that captures attention by serving something people genuinely care about.</p>
            </div>
            <div className="anchor-card">
              <div className="anchor-icon">🌊</div>
              <h3>Evolution Anchor</h3>
              <p>Your complete GTM execution and customer journey. Plan sustainable growth and retention strategy that deepens connection to your strategic impact over time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="journey" id="journey">
        <div className="container">
          <h2 className="section-title">From Discovery to Actionable Strategy</h2>
          <p className="section-subtitle">Samvadic takes you on a complete journey from discovering your brand's hidden potential to actionable elements that showcase your true competitive advantage.</p>
          
          <div className="journey-steps">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Brand Potential Discovery</h3>
              <p>AI-guided conversations reveal the deeper human needs and cultural currents your solution touches.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Strategic Anchor Development</h3>
              <p>Build your complete foundation rooted in human psychology and culture through our proven framework.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Brand Expression & GTM</h3>
              <p>Develop authentic brand voice, attention strategies, and go-to-market approach aligned with your strategic impact.</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <h3>Professional Documentation</h3>
              <p>Receive comprehensive strategy PDFs and investor pitch decks that highlight your brand's true potential.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Advisor Section */}
      <section className="advisor">
        <div className="container">
          <div className="advisor-content">
            <h2>Your Always-Available Strategic Advisor</h2>
            <p>After completing your <strong>Four Anchor Journey</strong>, Samvadic transforms into your expert strategic advisor — an AI that has comprehensive knowledge of your brand's strategy and can provide the best strategic advice.</p>
            <p>From campaign decisions to product launches, Samvadic ensures every choice strengthens your connection to the strategic impact that sets you apart from competitors.</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="container">
          <h2 className="section-title">Choose Your Journey</h2>
          <p className="section-subtitle">Pricing and plans coming soon. Join the waitlist to be first in line for early access.</p>
          
          <div style={{textAlign: 'center', marginTop: '3rem'}}>
            <Link to="/register" className="btn-primary">Join Waitlist</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to Discover What Your Brand Is Really Capable Of?</h2>
          <p>Join founders who unlocked strategic advantages they never knew existed.</p>
          <Link to="/register" className="btn-white">Start Your Strategic Journey →</Link>
        </div>
      </section>
    </div>
  )
}

export default LandingPage